import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import TVShowCard from "@/components/base/TVShowCard";
import { tvShows, categories } from "@/mocks/data";

const categoryNames: Record<string, string> = {
  xuanyi: "悬疑",
  dongzuo: "动作",
  aiqing: "爱情",
  kehuan: "科幻",
  xiju: "喜剧",
  kongbu: "恐怖",
};

export default function Home() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const heroParallax = scrollY * 0.4;

  const featuredShows = tvShows.filter((s) => s.rating >= 9.0).slice(0, 4);
  const latestShows = [...tvShows].sort((a, b) => b.year - a.year || b.id - a.id).slice(0, 6);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "红桃影视大全高清欧美剧 - 免费在线观看",
    description: "红桃影视大全高清欧美剧汇聚海量欧美热门剧集，提供1080P超清画质、中英双语字幕，涵盖悬疑、动作、爱情、科幻等全类型欧美剧。",
    url: "https://www.hongtao-tv.com/",
    isPartOf: {
      "@type": "WebSite",
      name: "红桃影视大全",
      url: "https://www.hongtao-tv.com",
    },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "红桃影视大全",
    url: "https://www.hongtao-tv.com",
    description: "红桃影视大全高清欧美剧汇聚海量欧美热门剧集，提供1080P超清画质、中英双语字幕。",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "400-888-8888",
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="relative h-[420px] md:h-[600px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=Cinematic%20home%20theater%20room%20with%20large%20screen%20displaying%20dramatic%20movie%20scene%2C%20warm%20ambient%20lighting%2C%20cozy%20modern%20sofa%20and%20popcorn%2C%20dark%20background%20with%20subtle%20golden%20and%20crimson%20glow%2C%20professional%20interior%20design%20photography%2C%20luxurious%20and%20inviting%20atmosphere%2C%20high%20detail%2C%20soft%20shadows&width=1600&height=900&seq=hero-bg-01&orientation=landscape)`,
              transform: `translateY(${heroParallax}px)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4 leading-tight">
              红桃影视大全 <span className="text-primary-400">高清欧美剧</span>
            </h1>
            <p className="text-background-50/80 text-sm md:text-lg max-w-2xl mb-8 leading-relaxed">
              汇聚海量欧美热门剧集，1080P超清画质，中英双语字幕<br className="hidden sm:block" />
              涵盖悬疑、动作、爱情、科幻等全类型，随时随地免费在线畅看
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/xuanyi"
                className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap text-sm"
              >
                开始追剧
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-background-50/15 text-background-50 font-medium rounded-full hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap text-sm backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto px-4 md:px-6 py-10 md:py-14" data-product-shop>
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              热门推荐
            </h2>
            <p className="text-foreground-500 text-sm mt-2">评分最高、口碑最好的欧美剧集精选</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredShows.map((show) => (
              <TVShowCard key={show.id} show={show} />
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                剧集分类
              </h2>
              <p className="text-foreground-500 text-sm mt-2">按类型快速找到你想看的欧美剧</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-lg p-5 text-center border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300 mb-3">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-400">{cat.count}部剧集</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto px-4 md:px-6 py-10 md:py-14" data-product-shop>
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              最新更新
            </h2>
            <p className="text-foreground-500 text-sm mt-2">第一时间追更最新上线的欧美剧集</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latestShows.map((show) => (
              <TVShowCard key={show.id} show={show} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/xuanyi"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-100 text-secondary-900 font-medium rounded-full hover:bg-secondary-200 transition-all duration-300 whitespace-nowrap text-sm"
            >
              查看全部剧集
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <section className="bg-foreground-900 py-12 md:py-16">
          <div className="mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
              为什么选择<span className="text-primary-400">红桃影视大全</span>？
            </h2>
            <p className="text-background-50/60 text-sm mb-10">
              我们致力于为国内欧美剧爱好者提供最优质的观剧体验
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: "ri-4k-line", title: "1080P超清画质", desc: "全高清画质，每一个细节都清晰可见" },
                { icon: "ri-closed-captioning-line", title: "中英双语字幕", desc: "精准翻译，双语对照学习无忧" },
                { icon: "ri-flashlight-line", title: "极速更新", desc: "海外播出后24小时内上线中字" },
                { icon: "ri-smartphone-line", title: "多端观看", desc: "电脑平板手机全平台畅享" },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-background-50 text-sm mb-2">{item.title}</h3>
                  <p className="text-background-50/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}